/**
 * Revenue KPI tiles — one row of tiles per `totals[]` currency (TMMPDM-0020, MM-D-100).
 * TMMPDM-0037 (tmm-api 0099, no revenue share): Retail · Wholesale (our price) · Reseller margin ·
 * Reseller net (after affiliates, `tenantShareMinor`) · Tax · Refunds. Uplift and Platform share
 * are dropped — they now equal the margin and the wholesale.
 */
import { Box, Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { KpiCard } from '@/components/ui';
import { formatMoneyWhole } from '@/format';
import type { CurrencyRevenueTotals } from '@/api/tmm/types';

interface RevenueTotalsProps {
  totals: CurrencyRevenueTotals[] | null;
  locale: string;
  loading: boolean;
}

const KPI_KEYS = [
  { key: 'retail', field: 'retailMinor' },
  { key: 'wholesale', field: 'wholesaleMinor' },
  { key: 'resellerMargin', field: 'resellerMarginMinor' },
  { key: 'resellerNet', field: 'tenantShareMinor' },
  { key: 'tax', field: 'taxMinor' },
  { key: 'refunds', field: 'refundsMinor' },
] as const;

const KPI_FALLBACK: Record<(typeof KPI_KEYS)[number]['key'], string> = {
  retail: 'Retail',
  wholesale: 'Wholesale (our price)',
  resellerMargin: 'Reseller margin',
  resellerNet: 'Reseller net',
  tax: 'Tax',
  refunds: 'Refunds',
};

export function RevenueTotals({ totals, locale, loading }: RevenueTotalsProps) {
  const { t } = useCopy();

  if (!totals) {
    return (
      <Typography variant="body2" color="text.secondary">
        {loading ? t('common.loading', 'Loading') : null}
      </Typography>
    );
  }

  if (totals.length === 0) {
    return null;
  }

  return (
    <Box>
      {totals.map((entry) => (
        <Box key={entry.currency} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {entry.currency}
          </Typography>
          <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap' }}>
            {KPI_KEYS.map(({ key, field }) => (
              <Box key={key} sx={{ flex: '1 1 160px' }}>
                <KpiCard
                  label={t(`revenue.kpi.${key}`, KPI_FALLBACK[key])}
                  value={formatMoneyWhole(entry[field], entry.currency, locale)}
                  {...(key === 'resellerNet'
                    ? { hint: t('revenue.afterAffiliates', 'after affiliates') }
                    : {})}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
